import { useState } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Settings, Mail, Loader2, Trash2, AlertTriangle, Send, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { TutorialAccountsCard } from './TutorialAccountsCard';
import { QRCodesCard } from './QRCodesCard';

export function AdminSettingsTab({ settings, setSettings, onSaveSettings }) {
  const [sendingDigest, setSendingDigest] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgeConfirmation, setPurgeConfirmation] = useState('');
  const [purging, setPurging] = useState(false);

  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [emailDiag, setEmailDiag] = useState(null);
  const [emailLogs, setEmailLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const handleTestEmail = async () => {
    if (!testEmail.trim()) { toast.error('Enter an email address'); return; }
    setSendingTest(true);
    try {
      const res = await api.post('/admin/test-email', { email: testEmail.trim() });
      setEmailDiag(res.data);
      if (res.data.success) {
        toast.success(`Test email sent to ${testEmail}`);
      } else {
        toast.error('Email send failed, check config below');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally { setSendingTest(false); }
  };

  const fetchEmailLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await api.get('/admin/email-logs');
      // Response is now {logs, summary}; the endpoint used to return a bare array.
      // Support both shapes so an agent can roll this part forward or back safely.
      const logs = Array.isArray(res.data) ? res.data : (res.data?.logs || []);
      setEmailLogs(logs);
      setShowLogs(true);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally { setLoadingLogs(false); }
  };

  const handleSendDigest = async () => {
    if (!window.confirm('This will trigger the monthly digest for ALL approved providers. Continue?')) return;
    setSendingDigest(true);
    try {
      const res = await api.post('/admin/send-monthly-digest');
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger digest');
    } finally { setSendingDigest(false); }
  };

  if (!settings) return null;

  return (
    <div className="max-w-xl space-y-6">
      <TutorialAccountsCard />
      <QRCodesCard />

      <div className="card-surface p-6">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-[#D4AF37]" />
          Platform Settings
        </h3>
        <div className="space-y-5">
          <div>
            <Label className="text-sm font-medium">Commission Rate (%)</Label>
            <p className="text-xs text-stone-400 mb-2">Platform fee taken from each booking (0-50%)</p>
            <Input
              type="number"
              min={0} max={50} step={1}
              value={settings.commission_rate}
              onChange={e => setSettings({ ...settings, commission_rate: parseFloat(e.target.value) || 0 })}
              className="input-zuriya w-32"
              data-testid="commission-rate-input"
            />
          </div>
          <div className="flex items-center justify-between py-3 border-t border-[#E6E4DD]">
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">Bookings Enabled</p>
              <p className="text-xs text-stone-400">Allow new bookings on the platform</p>
            </div>
            <Switch checked={settings.bookings_enabled} onCheckedChange={v => setSettings({ ...settings, bookings_enabled: v })} data-testid="toggle-bookings" />
          </div>
          <div className="flex items-center justify-between py-3 border-t border-[#E6E4DD]">
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">Registrations Enabled</p>
              <p className="text-xs text-stone-400">Allow new user registrations</p>
            </div>
            <Switch checked={settings.registrations_enabled} onCheckedChange={v => setSettings({ ...settings, registrations_enabled: v })} data-testid="toggle-registrations" />
          </div>
          <div className="flex items-center justify-between py-3 border-t border-[#E6E4DD]">
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">Video Feed Enabled</p>
              <p className="text-xs text-stone-400">Show the TikTok-style video feed</p>
            </div>
            <Switch checked={settings.video_feed_enabled} onCheckedChange={v => setSettings({ ...settings, video_feed_enabled: v })} data-testid="toggle-video-feed" />
          </div>
          <div className="flex items-center justify-between py-3 border-t border-[#E6E4DD]">
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">Guest Checkout Enabled</p>
              <p className="text-xs text-stone-400">Allow bookings without account creation</p>
            </div>
            <Switch checked={settings.guest_checkout_enabled} onCheckedChange={v => setSettings({ ...settings, guest_checkout_enabled: v })} data-testid="toggle-guest-checkout" />
          </div>
        </div>
        <Button onClick={onSaveSettings} className="btn-primary mt-6" data-testid="save-settings-btn">Save Settings</Button>
      </div>

      {/* Monthly Digest */}
      <div className="card-surface p-6">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#D4AF37]" />
          Provider Monthly Digest
        </h3>
        <p className="text-sm text-stone-500 mb-4">
          Trigger a monthly analytics summary for all approved providers.
        </p>
        <Button onClick={handleSendDigest} disabled={sendingDigest} className="btn-primary gap-2" data-testid="send-monthly-digest-btn">
          {sendingDigest ? <><Loader2 className="h-4 w-4 animate-spin" />Sending...</> : <><Mail className="h-4 w-4" />Send Monthly Digests</>}
        </Button>
      </div>

      {/* Email Diagnostics */}
      <div className="card-surface p-6" data-testid="email-diagnostics">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
          <Send className="h-5 w-5 text-[#D4AF37]" />
          Email Diagnostics (SendGrid)
        </h3>
        <div className="flex gap-2 mb-4">
          <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Enter email to send test..." className="input-zuriya flex-1" data-testid="test-email-input" />
          <Button onClick={handleTestEmail} disabled={sendingTest} className="btn-primary gap-2" data-testid="send-test-email-btn">
            {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Test
          </Button>
        </div>
        {emailDiag && (
          <div className={`p-4 rounded-lg border mb-4 ${emailDiag.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="email-diag-result">
            <div className="flex items-center gap-2 mb-2">
              {emailDiag.success ? <CheckCircle className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
              <span className={`font-medium ${emailDiag.success ? 'text-green-700' : 'text-red-700'}`}>
                {emailDiag.success ? 'Email sent successfully' : 'Email failed to send'}
              </span>
            </div>
            <div className="text-xs space-y-1 text-stone-600">
              <p>Provider: <span className="font-mono">{emailDiag.config?.provider}</span></p>
              <p>Sender: <span className="font-mono">{emailDiag.config?.sender_email || 'Not set'}</span></p>
              <p>API Key: {emailDiag.config?.api_key_set ? <span className="text-green-600 font-medium">Configured</span> : <span className="text-red-500 font-medium">NOT SET</span>}</p>
            </div>
          </div>
        )}
        <Button variant="outline" onClick={fetchEmailLogs} disabled={loadingLogs} className="gap-2 border-[#E6E4DD]" data-testid="view-email-logs-btn">
          {loadingLogs ? <Loader2 className="h-4 w-4 animate-spin" /> : showLogs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {showLogs ? 'Refresh Logs' : 'View Email Logs'}
        </Button>
        {showLogs && emailLogs && (
          <div className="mt-4 overflow-x-auto" data-testid="email-logs-table">
            {emailLogs.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">No email logs found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#E6E4DD]">
                  <th className="text-left py-2 px-3 text-stone-500 font-medium">Status</th>
                  <th className="text-left py-2 px-3 text-stone-500 font-medium">To</th>
                  <th className="text-left py-2 px-3 text-stone-500 font-medium">Subject</th>
                  <th className="text-left py-2 px-3 text-stone-500 font-medium">Time</th>
                  <th className="text-left py-2 px-3 text-stone-500 font-medium">Error</th>
                </tr></thead>
                <tbody>
                  {emailLogs.map((log, i) => (
                    <tr key={`log-${i}`} className={`border-b border-[#E6E4DD] ${!log.success ? 'bg-red-50' : 'hover:bg-[#F9F6F0]'}`}>
                      <td className="py-2 px-3">{log.success ? <span className="text-green-600 flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" />Sent</span> : <span className="text-red-600 flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />Failed</span>}</td>
                      <td className="py-2 px-3 text-stone-600 font-mono text-xs">{log.to}</td>
                      <td className="py-2 px-3 text-stone-600 text-xs truncate max-w-[200px]">{log.subject}</td>
                      <td className="py-2 px-3 text-stone-500 text-xs whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</td>
                      <td className="py-2 px-3 text-red-500 text-xs truncate max-w-[250px]" title={log.error || ''}>{log.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Danger Zone, Purge All Data */}
      <div className="card-surface p-6 border-2 border-red-200">
        <h3 className="font-heading text-lg font-medium text-red-600 mb-2 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Danger Zone
        </h3>
        <p className="text-sm text-stone-500 mb-4">
          Permanently delete <strong>all providers, clients, bookings, services, reviews</strong>, and associated data. Only the admin account will be preserved. This action cannot be undone.
        </p>
        <Button
          variant="outline"
          onClick={() => { setShowPurgeDialog(true); setPurgeConfirmation(''); }}
          className="border-red-300 text-red-600 hover:bg-red-50 gap-2"
          data-testid="open-purge-dialog-btn"
        >
          <Trash2 className="h-4 w-4" />
          Purge All Data
        </Button>
      </div>

      {/* Purge Confirmation Dialog */}
      <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Permanently Delete All Data?
            </DialogTitle>
            <DialogDescription className="text-stone-600 pt-2">
              This will permanently delete:
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-stone-600 space-y-1 pl-4 list-disc">
            <li>All provider accounts and profiles</li>
            <li>All client accounts</li>
            <li>All bookings and payments</li>
            <li>All services, reviews, and messages</li>
            <li>All uploaded files (portfolio, videos, images)</li>
          </ul>
          <p className="text-sm text-stone-600 mt-2">
            <strong>Your admin account will be preserved.</strong>
          </p>
          <div className="mt-4">
            <Label className="text-sm font-medium text-red-600">
              Type <span className="font-mono bg-red-50 px-1.5 py-0.5 rounded">DELETE ALL DATA</span> to confirm
            </Label>
            <Input
              value={purgeConfirmation}
              onChange={(e) => setPurgeConfirmation(e.target.value)}
              placeholder="DELETE ALL DATA"
              className="mt-2 font-mono border-red-200 focus:ring-red-400"
              data-testid="purge-confirmation-input"
            />
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPurgeDialog(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={purgeConfirmation !== 'DELETE ALL DATA' || purging}
              onClick={async () => {
                setPurging(true);
                try {
                  const res = await api.post('/admin/purge-all-data', { confirmation: purgeConfirmation });
                  toast.success(res.data.message);
                  setShowPurgeDialog(false);
                  setPurgeConfirmation('');
                  // Reload page to refresh all stats
                  window.location.reload();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Purge failed');
                } finally {
                  setPurging(false);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
              data-testid="confirm-purge-btn"
            >
              {purging ? (
                <><Loader2 className="h-4 w-4 animate-spin" />Purging...</>
              ) : (
                <><Trash2 className="h-4 w-4" />Permanently Delete All</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
